/**
 * SSRDataContext – Shared context for server-side rendered data.
 * Used by entry-server.tsx (Provider) and data hooks (Consumer).
 * On the client, reads from window.__SSR_DATA__ if available.
 */
import { createContext, useContext, type ReactNode } from "react";

export interface SSRData {
  blogPosts?: any[];
  immobilien?: any[];
}

const SSRDataContext = createContext<SSRData | undefined>(undefined);

export function SSRDataProvider({ data, children }: { data?: SSRData; children: ReactNode }) {
  return (
    <SSRDataContext.Provider value={data}>
      {children}
    </SSRDataContext.Provider>
  );
}

/**
 * Hook to access SSR-prefetched data.
 * Returns undefined if no SSR data is available (pure client-side navigation).
 */
export function useSSRData(): SSRData | undefined {
  return useContext(SSRDataContext);
}

/**
 * Gets initial SSR data from window.__SSR_DATA__ (injected by server).
 * Used in main.tsx to bootstrap the SSRDataProvider on the client.
 */
export function getClientSSRData(): SSRData | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as any).__SSR_DATA__ as SSRData | undefined;
}
